import React, { useEffect, useRef } from 'react';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { BoldIcon, ItalicIcon, UnderlineIcon, Heading2Icon, Heading3Icon, ListIcon, ListOrderedIcon, LinkIcon, QuoteIcon, PlusIcon, Trash2Icon } from 'lucide-react';
// Helper for updating content
const useBlockUpdate = (content: any, onChange: (content: any) => void) => {
  return (key: string, value: any) => onChange({
    ...content,
    [key]: value
  });
};
export function HeroBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-758">
      <Input label="Title" value={content.title || ''} onChange={e => update('title', e.target.value)} data-id="element-759" />
      <Input label="Subtitle" value={content.subtitle || ''} onChange={e => update('subtitle', e.target.value)} data-id="element-760" />
      <Input label="Background Image URL" value={content.backgroundImage || ''} onChange={e => update('backgroundImage', e.target.value)} data-id="element-761" />
      {content.backgroundImage && <img src={content.backgroundImage} alt="Preview" className="h-32 w-full object-cover border border-border" data-id="element-762" />}
      <div data-id="element-763">
        <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-764">
          Overlay Opacity ({content.overlayOpacity || 50}%)
        </label>
        <input type="range" min="0" max="100" value={content.overlayOpacity || 50} onChange={e => update('overlayOpacity', parseInt(e.target.value))} className="w-full accent-primary" data-id="element-765" />
      </div>
      <Select label="Alignment" value={content.alignment || 'center'} onChange={e => update('alignment', e.target.value)} options={[{
      label: 'Left',
      value: 'left'
    }, {
      label: 'Center',
      value: 'center'
    }, {
      label: 'Right',
      value: 'right'
    }]} data-id="element-766" />
      <div className="grid grid-cols-2 gap-4" data-id="element-767">
        <Input label="CTA Text" value={content.ctaText || ''} onChange={e => update('ctaText', e.target.value)} data-id="element-768" />
        <Input label="CTA Link" value={content.ctaLink || ''} onChange={e => update('ctaLink', e.target.value)} data-id="element-769" />
      </div>
    </div>;
}
export function RichTextBlockEditor({
  content,
  onChange
}: any) {
  const editorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (content.html || '')) {
      editorRef.current.innerHTML = content.html || '<p>Start typing...</p>';
    }
  }, []);
  const handleInput = () => {
    if (editorRef.current) {
      onChange({
        ...content,
        html: editorRef.current.innerHTML
      });
    }
  };
  const exec = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };
  const addLink = () => {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  };
  const ToolbarBtn = ({
    icon: Icon,
    onClick,
    title
  }: any) => <button onClick={onClick} title={title} className="p-2 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-id="element-770">
      <Icon className="w-4 h-4" data-id="element-771" />
    </button>;
  return <div className="border border-border bg-background" data-id="element-772">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-1 bg-card" data-id="element-773">
        <ToolbarBtn icon={BoldIcon} onClick={() => exec('bold')} title="Bold" data-id="element-774" />
        <ToolbarBtn icon={ItalicIcon} onClick={() => exec('italic')} title="Italic" data-id="element-775" />
        <ToolbarBtn icon={UnderlineIcon} onClick={() => exec('underline')} title="Underline" data-id="element-776" />
        <div className="w-px h-4 bg-border mx-1" data-id="element-777" />
        <ToolbarBtn icon={Heading2Icon} onClick={() => exec('formatBlock', 'H2')} title="Heading 2" data-id="element-778" />
        <ToolbarBtn icon={Heading3Icon} onClick={() => exec('formatBlock', 'H3')} title="Heading 3" data-id="element-779" />
        <div className="w-px h-4 bg-border mx-1" data-id="element-780" />
        <ToolbarBtn icon={ListIcon} onClick={() => exec('insertUnorderedList')} title="Bullet List" data-id="element-781" />
        <ToolbarBtn icon={ListOrderedIcon} onClick={() => exec('insertOrderedList')} title="Numbered List" data-id="element-782" />
        <div className="w-px h-4 bg-border mx-1" data-id="element-783" />
        <ToolbarBtn icon={LinkIcon} onClick={addLink} title="Link" data-id="element-784" />
        <ToolbarBtn icon={QuoteIcon} onClick={() => exec('formatBlock', 'BLOCKQUOTE')} title="Quote" data-id="element-785" />
      </div>
      <div ref={editorRef} contentEditable onInput={handleInput} onBlur={handleInput} className="p-4 min-h-[150px] focus:outline-none prose prose-invert max-w-none font-mono text-sm leading-relaxed" data-id="element-786" />
    </div>;
}
export function ImageBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-787">
      <Input label="Image URL" value={content.url || ''} onChange={e => update('url', e.target.value)} data-id="element-788" />
      {content.url && <img src={content.url} alt="Preview" className="h-48 w-full border border-border" style={{
      objectFit: content.objectFit || 'cover',
      objectPosition: content.objectPosition || 'center'
    }} data-id="element-789" />}
      <Input label="Alt Text" value={content.alt || ''} onChange={e => update('alt', e.target.value)} data-id="element-790" />
      <Input label="Caption" value={content.caption || ''} onChange={e => update('caption', e.target.value)} data-id="element-791" />
      <div className="grid grid-cols-2 gap-4" data-id="element-792">
        <Select label="Aspect Ratio" value={content.aspectRatio || 'auto'} onChange={e => update('aspectRatio', e.target.value)} options={[{
        label: 'Auto',
        value: 'auto'
      }, {
        label: '16:9',
        value: '16:9'
      }, {
        label: '4:3',
        value: '4:3'
      }, {
        label: '1:1',
        value: '1:1'
      }, {
        label: '3:2',
        value: '3:2'
      }]} data-id="element-793" />
        <Select label="Object Fit" value={content.objectFit || 'cover'} onChange={e => update('objectFit', e.target.value)} options={[{
        label: 'Cover',
        value: 'cover'
      }, {
        label: 'Contain',
        value: 'contain'
      }]} data-id="element-794" />
      </div>

      {/* Visual Position Picker */}
      <div data-id="element-795">
        <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-796">
          Image Position
        </label>
        <div className="grid grid-cols-3 gap-1 w-32 h-32 border border-border p-1 bg-secondary/30" data-id="element-797">
          {['top left', 'top center', 'top right', 'center left', 'center', 'center right', 'bottom left', 'bottom center', 'bottom right'].map(pos => <button key={pos} type="button" onClick={() => update('objectPosition', pos)} className={`w-full h-full border transition-colors ${(content.objectPosition || 'center') === pos ? 'bg-primary border-primary' : 'bg-card border-border hover:bg-secondary'}`} title={pos} data-id="element-798" />)}
        </div>
        <p className="font-mono text-[10px] text-muted-foreground mt-2" data-id="element-799">
          Controls which part of the image is visible when cropped.
        </p>
      </div>
    </div>;
}
export function GalleryBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  const images = content.images || [];
  const addImage = () => update('images', [...images, {
    url: '',
    alt: '',
    caption: ''
  }]);
  const updateImage = (index: number, key: string, value: string) => {
    const newImages = [...images];
    newImages[index] = {
      ...newImages[index],
      [key]: value
    };
    update('images', newImages);
  };
  const removeImage = (index: number) => update('images', images.filter((_: any, i: number) => i !== index));
  return <div className="space-y-6" data-id="element-800">
      <div className="grid grid-cols-2 gap-4" data-id="element-801">
        <Select label="Columns" value={content.columns || '3'} onChange={e => update('columns', e.target.value)} options={[{
        label: '1',
        value: '1'
      }, {
        label: '2',
        value: '2'
      }, {
        label: '3',
        value: '3'
      }, {
        label: '4',
        value: '4'
      }]} data-id="element-802" />
        <Select label="Gap Size" value={content.gap || 'medium'} onChange={e => update('gap', e.target.value)} options={[{
        label: 'Small',
        value: 'small'
      }, {
        label: 'Medium',
        value: 'medium'
      }, {
        label: 'Large',
        value: 'large'
      }]} data-id="element-803" />
      </div>

      <div className="space-y-4" data-id="element-804">
        {images.map((img: any, i: number) => <div key={i} className="p-4 border border-border bg-card relative space-y-4" data-id="element-805">
            <button onClick={() => removeImage(i)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive" data-id="element-806">
              <Trash2Icon className="w-4 h-4" data-id="element-807" />
            </button>
            <Input label="Image URL" value={img.url} onChange={e => updateImage(i, 'url', e.target.value)} data-id="element-808" />
            {img.url && <img src={img.url} alt="Preview" className="h-24 object-cover border border-border" data-id="element-809" />}
            <div className="grid grid-cols-2 gap-4" data-id="element-810">
              <Input label="Alt Text" value={img.alt} onChange={e => updateImage(i, 'alt', e.target.value)} data-id="element-811" />
              <Input label="Caption" value={img.caption} onChange={e => updateImage(i, 'caption', e.target.value)} data-id="element-812" />
            </div>
          </div>)}
      </div>
      <Button onClick={addImage} variant="outline" className="w-full gap-2" data-id="element-813">
        <PlusIcon className="w-4 h-4" data-id="element-814" /> Add Image
      </Button>
    </div>;
}
export function VideoBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-815">
      <Input label="Video URL (YouTube/Vimeo/MP4)" value={content.url || ''} onChange={e => update('url', e.target.value)} data-id="element-816" />
      <Textarea label="Custom Embed Code" value={content.embedCode || ''} onChange={e => update('embedCode', e.target.value)} rows={3} data-id="element-817" />
      <Select label="Aspect Ratio" value={content.aspectRatio || '16:9'} onChange={e => update('aspectRatio', e.target.value)} options={[{
      label: '16:9',
      value: '16:9'
    }, {
      label: '4:3',
      value: '4:3'
    }, {
      label: '21:9',
      value: '21:9'
    }]} data-id="element-818" />
      <div className="flex gap-4" data-id="element-819">
        <label className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-820">
          <input type="checkbox" checked={content.autoplay || false} onChange={e => update('autoplay', e.target.checked)} className="accent-primary" data-id="element-821" />{' '}
          Autoplay
        </label>
        <label className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-822">
          <input type="checkbox" checked={content.loop || false} onChange={e => update('loop', e.target.checked)} className="accent-primary" data-id="element-823" />{' '}
          Loop
        </label>
      </div>
    </div>;
}
export function MetricsBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  const metrics = content.metrics || [];
  const addMetric = () => update('metrics', [...metrics, {
    value: '',
    label: '',
    description: ''
  }]);
  const updateMetric = (index: number, key: string, value: string) => {
    const newMetrics = [...metrics];
    newMetrics[index] = {
      ...newMetrics[index],
      [key]: value
    };
    update('metrics', newMetrics);
  };
  const removeMetric = (index: number) => update('metrics', metrics.filter((_: any, i: number) => i !== index));
  return <div className="space-y-6" data-id="element-824">
      <Select label="Columns" value={content.columns || '3'} onChange={e => update('columns', e.target.value)} options={[{
      label: '2',
      value: '2'
    }, {
      label: '3',
      value: '3'
    }, {
      label: '4',
      value: '4'
    }]} data-id="element-825" />
      <div className="space-y-4" data-id="element-826">
        {metrics.map((m: any, i: number) => <div key={i} className="p-4 border border-border bg-card relative grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-827">
            <button onClick={() => removeMetric(i)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive" data-id="element-828">
              <Trash2Icon className="w-4 h-4" data-id="element-829" />
            </button>
            <Input label="Value (e.g. 40%)" value={m.value} onChange={e => updateMetric(i, 'value', e.target.value)} data-id="element-830" />
            <Input label="Label" value={m.label} onChange={e => updateMetric(i, 'label', e.target.value)} data-id="element-831" />
            <Input label="Description" value={m.description} onChange={e => updateMetric(i, 'description', e.target.value)} data-id="element-832" />
          </div>)}
      </div>
      <Button onClick={addMetric} variant="outline" className="w-full gap-2" data-id="element-833">
        <PlusIcon className="w-4 h-4" data-id="element-834" /> Add Metric
      </Button>
    </div>;
}
export function QuoteBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-835">
      <Textarea label="Quote Text" value={content.quote || ''} onChange={e => update('quote', e.target.value)} rows={4} data-id="element-836" />
      <div className="grid grid-cols-2 gap-4" data-id="element-837">
        <Input label="Author Name" value={content.authorName || ''} onChange={e => update('authorName', e.target.value)} data-id="element-838" />
        <Input label="Author Role" value={content.authorRole || ''} onChange={e => update('authorRole', e.target.value)} data-id="element-839" />
      </div>
      <Input label="Author Image URL" value={content.authorImage || ''} onChange={e => update('authorImage', e.target.value)} data-id="element-840" />
      <Select label="Variant" value={content.variant || 'simple'} onChange={e => update('variant', e.target.value)} options={[{
      label: 'Simple',
      value: 'simple'
    }, {
      label: 'Card',
      value: 'card'
    }, {
      label: 'Large',
      value: 'large'
    }]} data-id="element-841" />
    </div>;
}
export function TimelineBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  const steps = content.steps || [];
  const addStep = () => update('steps', [...steps, {
    title: '',
    description: '',
    label: ''
  }]);
  const updateStep = (index: number, key: string, value: string) => {
    const newSteps = [...steps];
    newSteps[index] = {
      ...newSteps[index],
      [key]: value
    };
    update('steps', newSteps);
  };
  const removeStep = (index: number) => update('steps', steps.filter((_: any, i: number) => i !== index));
  return <div className="space-y-6" data-id="element-842">
      <Select label="Layout" value={content.layout || 'vertical'} onChange={e => update('layout', e.target.value)} options={[{
      label: 'Vertical',
      value: 'vertical'
    }, {
      label: 'Horizontal',
      value: 'horizontal'
    }]} data-id="element-843" />
      <div className="space-y-4" data-id="element-844">
        {steps.map((s: any, i: number) => <div key={i} className="p-4 border border-border bg-card relative space-y-4" data-id="element-845">
            <button onClick={() => removeStep(i)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive" data-id="element-846">
              <Trash2Icon className="w-4 h-4" data-id="element-847" />
            </button>
            <div className="grid grid-cols-2 gap-4" data-id="element-848">
              <Input label="Label (e.g. 01, Research)" value={s.label} onChange={e => updateStep(i, 'label', e.target.value)} data-id="element-849" />
              <Input label="Title" value={s.title} onChange={e => updateStep(i, 'title', e.target.value)} data-id="element-850" />
            </div>
            <Textarea label="Description" value={s.description} onChange={e => updateStep(i, 'description', e.target.value)} rows={2} data-id="element-851" />
          </div>)}
      </div>
      <Button onClick={addStep} variant="outline" className="w-full gap-2" data-id="element-852">
        <PlusIcon className="w-4 h-4" data-id="element-853" /> Add Step
      </Button>
    </div>;
}
export function TwoColumnBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-6" data-id="element-854">
      <div className="grid grid-cols-2 gap-4" data-id="element-855">
        <Select label="Column Ratio" value={content.ratio || '50-50'} onChange={e => update('ratio', e.target.value)} options={[{
        label: '50 / 50',
        value: '50-50'
      }, {
        label: '60 / 40',
        value: '60-40'
      }, {
        label: '40 / 60',
        value: '40-60'
      }]} data-id="element-856" />
        <div className="flex items-end pb-2" data-id="element-857">
          <label className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-858">
            <input type="checkbox" checked={content.reverseMobile || false} onChange={e => update('reverseMobile', e.target.checked)} className="accent-primary" data-id="element-859" />{' '}
            Reverse on Mobile
          </label>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-860">
        <div className="space-y-2" data-id="element-861">
          <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest block" data-id="element-862">
            Left Content (HTML)
          </label>
          <Textarea value={content.leftContent || ''} onChange={e => update('leftContent', e.target.value)} rows={8} placeholder="<p>Text goes here...</p>" data-id="element-863" />
        </div>
        <div className="space-y-4" data-id="element-864">
          <Input label="Right Image URL" value={content.imageUrl || ''} onChange={e => update('imageUrl', e.target.value)} data-id="element-865" />
          {content.imageUrl && <img src={content.imageUrl} alt="Preview" className="h-32 w-full object-cover border border-border" data-id="element-866" />}
          <Input label="Image Alt Text" value={content.imageAlt || ''} onChange={e => update('imageAlt', e.target.value)} data-id="element-867" />
        </div>
      </div>
    </div>;
}
export function CTABlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-868">
      <Input label="Heading" value={content.heading || ''} onChange={e => update('heading', e.target.value)} data-id="element-869" />
      <Textarea label="Description" value={content.description || ''} onChange={e => update('description', e.target.value)} rows={3} data-id="element-870" />
      <div className="grid grid-cols-2 gap-4" data-id="element-871">
        <Input label="Button Text" value={content.buttonText || ''} onChange={e => update('buttonText', e.target.value)} data-id="element-872" />
        <Input label="Button Link" value={content.buttonLink || ''} onChange={e => update('buttonLink', e.target.value)} data-id="element-873" />
      </div>
      <Select label="Variant" value={content.variant || 'simple'} onChange={e => update('variant', e.target.value)} options={[{
      label: 'Simple',
      value: 'simple'
    }, {
      label: 'Card',
      value: 'card'
    }, {
      label: 'Full Width',
      value: 'full-width'
    }]} data-id="element-874" />
    </div>;
}
export function DividerBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="grid grid-cols-2 gap-4" data-id="element-875">
      <Select label="Type" value={content.type || 'line'} onChange={e => update('type', e.target.value)} options={[{
      label: 'Line',
      value: 'line'
    }, {
      label: 'Empty Space',
      value: 'space'
    }, {
      label: 'Dots',
      value: 'dots'
    }]} data-id="element-876" />
      <Select label="Height/Spacing" value={content.height || 'medium'} onChange={e => update('height', e.target.value)} options={[{
      label: 'Small',
      value: 'small'
    }, {
      label: 'Medium',
      value: 'medium'
    }, {
      label: 'Large',
      value: 'large'
    }]} data-id="element-877" />
    </div>;
}
export function FeatureHighlightsBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  const features = content.features || [];
  const addFeature = () => update('features', [...features, {
    title: '',
    description: '',
    icon: ''
  }]);
  const updateFeature = (index: number, key: string, value: string) => {
    const newFeatures = [...features];
    newFeatures[index] = {
      ...newFeatures[index],
      [key]: value
    };
    update('features', newFeatures);
  };
  const removeFeature = (index: number) => update('features', features.filter((_: any, i: number) => i !== index));
  return <div className="space-y-6" data-id="element-878">
      <Select label="Columns" value={content.columns || '3'} onChange={e => update('columns', e.target.value)} options={[{
      label: '2',
      value: '2'
    }, {
      label: '3',
      value: '3'
    }, {
      label: '4',
      value: '4'
    }]} data-id="element-879" />
      <div className="space-y-4" data-id="element-880">
        {features.map((f: any, i: number) => <div key={i} className="p-4 border border-border bg-card relative space-y-4" data-id="element-881">
            <button onClick={() => removeFeature(i)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive" data-id="element-882">
              <Trash2Icon className="w-4 h-4" data-id="element-883" />
            </button>
            <div className="grid grid-cols-2 gap-4" data-id="element-884">
              <Input label="Title" value={f.title} onChange={e => updateFeature(i, 'title', e.target.value)} data-id="element-885" />
              <Input label="Icon Name (lucide)" value={f.icon} onChange={e => updateFeature(i, 'icon', e.target.value)} data-id="element-886" />
            </div>
            <Textarea label="Description" value={f.description} onChange={e => updateFeature(i, 'description', e.target.value)} rows={2} data-id="element-887" />
          </div>)}
      </div>
      <Button onClick={addFeature} variant="outline" className="w-full gap-2" data-id="element-888">
        <PlusIcon className="w-4 h-4" data-id="element-889" /> Add Feature
      </Button>
    </div>;
}
export function BeforeAfterBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-6" data-id="element-890">
      <Select label="Layout" value={content.layout || 'side-by-side'} onChange={e => update('layout', e.target.value)} options={[{
      label: 'Side by Side',
      value: 'side-by-side'
    }, {
      label: 'Stacked',
      value: 'stacked'
    }]} data-id="element-891" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-892">
        <div className="space-y-4 p-4 border border-border bg-card" data-id="element-893">
          <h4 className="font-mono text-xs font-bold uppercase tracking-widest" data-id="element-894">
            Before
          </h4>
          <Input label="Image URL" value={content.beforeImage || ''} onChange={e => update('beforeImage', e.target.value)} data-id="element-895" />
          {content.beforeImage && <img src={content.beforeImage} alt="Before" className="h-24 w-full object-cover border border-border" data-id="element-896" />}
          <Input label="Label" value={content.beforeLabel || ''} onChange={e => update('beforeLabel', e.target.value)} data-id="element-897" />
        </div>
        <div className="space-y-4 p-4 border border-border bg-card" data-id="element-898">
          <h4 className="font-mono text-xs font-bold uppercase tracking-widest" data-id="element-899">
            After
          </h4>
          <Input label="Image URL" value={content.afterImage || ''} onChange={e => update('afterImage', e.target.value)} data-id="element-900" />
          {content.afterImage && <img src={content.afterImage} alt="After" className="h-24 w-full object-cover border border-border" data-id="element-901" />}
          <Input label="Label" value={content.afterLabel || ''} onChange={e => update('afterLabel', e.target.value)} data-id="element-902" />
        </div>
      </div>
    </div>;
}
// Fallback for other blocks
export function GenericListBlockEditor({
  content,
  onChange,
  listKey,
  fields
}: any) {
  const update = useBlockUpdate(content, onChange);
  const items = content[listKey] || [];
  const addItem = () => {
    const newItem: any = {};
    fields.forEach((f: any) => newItem[f.key] = '');
    update(listKey, [...items, newItem]);
  };
  const updateItem = (index: number, key: string, value: string) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [key]: value
    };
    update(listKey, newItems);
  };
  const removeItem = (index: number) => update(listKey, items.filter((_: any, i: number) => i !== index));
  return <div className="space-y-6" data-id="element-903">
      <div className="space-y-4" data-id="element-904">
        {items.map((item: any, i: number) => <div key={i} className="p-4 border border-border bg-card relative space-y-4" data-id="element-905">
            <button onClick={() => removeItem(i)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive" data-id="element-906">
              <Trash2Icon className="w-4 h-4" data-id="element-907" />
            </button>
            {fields.map((f: any) => f.type === 'textarea' ? <Textarea key={f.key} label={f.label} value={item[f.key]} onChange={e => updateItem(i, f.key, e.target.value)} rows={2} data-id="element-908" /> : <Input key={f.key} label={f.label} value={item[f.key]} onChange={e => updateItem(i, f.key, e.target.value)} data-id="element-909" />)}
          </div>)}
      </div>
      <Button onClick={addItem} variant="outline" className="w-full gap-2" data-id="element-910">
        <PlusIcon className="w-4 h-4" data-id="element-911" /> Add Item
      </Button>
    </div>;
}
export function ResearchBlockEditor({
  content,
  onChange
}: any) {
  return <GenericListBlockEditor content={content} onChange={onChange} listKey="insights" fields={[{
    key: 'title',
    label: 'Title'
  }, {
    key: 'finding',
    label: 'Finding',
    type: 'textarea'
  }, {
    key: 'source',
    label: 'Source'
  }]} data-id="element-912" />;
}
export function CompetitorBlockEditor({
  content,
  onChange
}: any) {
  return <GenericListBlockEditor content={content} onChange={onChange} listKey="competitors" fields={[{
    key: 'name',
    label: 'Competitor Name'
  }, {
    key: 'imageUrl',
    label: 'Logo URL'
  }, {
    key: 'strengths',
    label: 'Strengths',
    type: 'textarea'
  }, {
    key: 'weaknesses',
    label: 'Weaknesses',
    type: 'textarea'
  }]} data-id="element-913" />;
}
export function UserFlowBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-914">
      <Input label="Title" value={content.title || ''} onChange={e => update('title', e.target.value)} data-id="element-915" />
      <Textarea label="Description" value={content.description || ''} onChange={e => update('description', e.target.value)} rows={2} data-id="element-916" />
      <Input label="Flow Diagram URL" value={content.imageUrl || ''} onChange={e => update('imageUrl', e.target.value)} data-id="element-917" />
      <GenericListBlockEditor content={content} onChange={onChange} listKey="steps" fields={[{
      key: 'label',
      label: 'Step Label'
    }]} data-id="element-918" />
    </div>;
}
export function DesignSystemBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-919">
      <Input label="Title" value={content.title || ''} onChange={e => update('title', e.target.value)} data-id="element-920" />
      <Textarea label="Description" value={content.description || ''} onChange={e => update('description', e.target.value)} rows={2} data-id="element-921" />
      <Select label="Columns" value={content.columns || '3'} onChange={e => update('columns', e.target.value)} options={[{
      label: '2',
      value: '2'
    }, {
      label: '3',
      value: '3'
    }]} data-id="element-922" />
      <GenericListBlockEditor content={content} onChange={onChange} listKey="images" fields={[{
      key: 'url',
      label: 'Image URL'
    }]} data-id="element-923" />
    </div>;
}
export function WireframesBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-924">
      <Input label="Title" value={content.title || ''} onChange={e => update('title', e.target.value)} data-id="element-925" />
      <Textarea label="Description" value={content.description || ''} onChange={e => update('description', e.target.value)} rows={2} data-id="element-926" />
      <Select label="Layout" value={content.layout || 'grid'} onChange={e => update('layout', e.target.value)} options={[{
      label: 'Grid',
      value: 'grid'
    }, {
      label: 'Carousel',
      value: 'carousel'
    }]} data-id="element-927" />
      <GenericListBlockEditor content={content} onChange={onChange} listKey="images" fields={[{
      key: 'url',
      label: 'Image URL'
    }]} data-id="element-928" />
    </div>;
}
export function MockupsBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-929">
      <Input label="Title" value={content.title || ''} onChange={e => update('title', e.target.value)} data-id="element-930" />
      <Textarea label="Description" value={content.description || ''} onChange={e => update('description', e.target.value)} rows={2} data-id="element-931" />
      <Select label="Layout" value={content.layout || 'grid'} onChange={e => update('layout', e.target.value)} options={[{
      label: 'Grid',
      value: 'grid'
    }, {
      label: 'Masonry',
      value: 'masonry'
    }, {
      label: 'Carousel',
      value: 'carousel'
    }]} data-id="element-932" />
      <GenericListBlockEditor content={content} onChange={onChange} listKey="images" fields={[{
      key: 'url',
      label: 'Image URL'
    }, {
      key: 'caption',
      label: 'Caption'
    }, {
      key: 'objectPosition',
      label: 'Position (e.g. center, top left)'
    }]} data-id="element-933" />
    </div>;
}
export function EmbedBlockEditor({
  content,
  onChange
}: any) {
  const update = useBlockUpdate(content, onChange);
  return <div className="space-y-4" data-id="element-934">
      <Input label="Embed URL (YouTube, Vimeo, Figma, etc.)" value={content.url || ''} onChange={e => update('url', e.target.value)} placeholder="https://..." data-id="element-935" />
      <div className="text-center font-mono text-xs text-muted-foreground my-2" data-id="element-936">
        — OR —
      </div>
      <Textarea label="Custom HTML / iFrame Code" value={content.html || ''} onChange={e => update('html', e.target.value)} rows={4} placeholder='<iframe src="..." />' data-id="element-937" />
      <Select label="Aspect Ratio" value={content.aspectRatio || '16:9'} onChange={e => update('aspectRatio', e.target.value)} options={[{
      label: '16:9 (Video)',
      value: '16:9'
    }, {
      label: '4:3 (Standard)',
      value: '4:3'
    }, {
      label: '1:1 (Square)',
      value: '1:1'
    }, {
      label: 'Auto (Fit Content)',
      value: 'auto'
    }]} data-id="element-938" />
    </div>;
}